import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty } from '@/shared/ui/Panel'
import { Segmented } from '@/shared/ui/Segmented'
import { Strip } from '@/shared/ui/Strip'
import { Switch } from '@/shared/ui/Switch'
import { CopyIcon, PlusIcon } from '@/shared/ui/icons'
import type { InvoiceResponse } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { explain } from '@/shared/lib/errors'
import { crypto, fiat } from '@/shared/lib/format'
import { useMeta } from '@/shared/lib/hooks/useMeta'
import { INVOICE_VIEW } from '@/stores/InvoicesStore'
import { useAuth, useStores } from '@/stores/RootStore'
import page from './Page.module.css'
import styles from './Withdrawals.module.css'

/**
 * Выставленные счета.
 *
 * Экрана нет в макете, но ссылка на оплату без него живёт ровно
 * до перезагрузки вкладки: отправить её покупателю повторно
 * или проверить, оплачена ли она, было неоткуда.
 */
export const InvoicesPage = observer(function InvoicesPage() {
  useMeta('Платёжные ссылки')
  const { invoices: store } = useStores()
  const auth = useAuth()

  useEffect(() => { void store.page.load() }, [store])

  /*
    Ссылки выставляют и мерчант, и админ. Единственный выключатель —
    флаг аккаунта: админ может запретить ссылки конкретному мерчанту,
    и тогда кнопки нет. Прячем её не вместо проверки, а чтобы не
    предлагать того, что бекенд отклонит уже после заполнения формы.
  */
  const allowed = auth.user?.payment_links_enabled !== false

  return (
    <div className={page.wide}>
      <header className={styles.pageHead}>
        <h1 className={styles.h1}>Платёжные ссылки</h1>
        {allowed ? (
          <Button variant="primary" onClick={() => store.openCreate()}>
            <PlusIcon size={24} /> Создать
          </Button>
        ) : null}
      </header>

      <section className={styles.card}>
        {allowed ? null : (
          <Strip tone="warn">
            Платёжные ссылки для вашего аккаунта отключены администратором.
          </Strip>
        )}

        {store.page.error && !store.page.loaded ? (
          <Strip tone="bad"
                 aside={<Button variant="secondary" onClick={() => void store.page.load()}>Ещё раз</Button>}>
            {explain(store.page.error)}
          </Strip>
        ) : null}

        {store.items.length === 0 && store.page.loaded ? (
          <Empty title="Счетов пока нет"
                 action={<Link className={styles.link} to="/app">К обзору</Link>} />
        ) : (
          <>
            <ul className={styles.list}>
              {store.items.map((item) => <Row key={item.id} item={item} />)}
            </ul>
            <Pagination total={store.page.value?.total ?? 0} limit={store.limit}
                        offset={store.offset}
                        onChange={(offset) => store.goToOffset(offset)} />
          </>
        )}
      </section>

      <CreateDialog />
      <CreatedDialog />
    </div>
  )
})

/** Форма новой ссылки: сумма, валюта цены и два необязательных условия. */
const CreateDialog = observer(function CreateDialog() {
  const { invoices: store } = useStores()
  if (!store.creating) return null
  const draft = store.draft

  return (
    <Modal title="Новая платёжная ссылка"
           onClose={() => store.closeCreate()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.closeCreate()}>Отмена</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.submit()}>Создать</Button>
             </>
           }>
      <Field label="Сумма" numeric inputMode="decimal"
             placeholder="Укажите сумму"
             param={draft.fiat === 'rub' ? '₽' : '$'}
             value={draft.amount}
             onChange={(e) => store.patch({ amount: e.target.value })}
             error={store.errors.amount ?? null} />

      <div className={styles.pickRow}>
        <span className={styles.pickLabel}>Валюта цены</span>
        <Segmented ariaLabel="Валюта цены" value={draft.fiat}
                   options={[{ value: 'usd', label: 'USD' }, { value: 'rub', label: 'RUB' }]}
                   onChange={(fiat) => store.patch({ fiat })} />
      </div>

      {/* Допуск недоплаты. Курс между выдачей ссылки и оплатой успевает
          сдвинуться, и покупатель, отправивший ровно столько, сколько
          было написано, попадает в недоплату на копейки. */}
      <Field label="Допустимая недоплата" numeric inputMode="decimal" param="%"
             placeholder="0"
             note="Насколько покупатель может недоплатить, а счёт всё равно закроется оплаченным. Не больше 5%."
             value={draft.accuracy}
             onChange={(e) => store.patch({ accuracy: e.target.value })}
             error={store.errors.accuracy ?? null} />

      <Switch checked={draft.multiple} label="Разрешить оплату несколькими переводами"
              onChange={(multiple) => store.patch({ multiple })} />
    </Modal>
  )
})

/**
 * Готовая ссылка.
 *
 * Отдельным окном, а не всплывающим сообщением: её надо скопировать и
 * отдать покупателю. Показана полностью — мерчант сверяет домен, прежде
 * чем послать ссылку клиенту.
 */
const CreatedDialog = observer(function CreatedDialog() {
  const { invoices: store } = useStores()
  const invoice = store.created
  if (invoice === null) return null

  return (
    <Modal title="Ссылка готова"
           note={`Счёт на ${fiat(invoice.amount_fiat, invoice.fiat_currency)}, действует до ${when(invoice.expires_at)}`}
           onClose={() => store.closeCreated()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.closeCreated()}>Закрыть</Button>
               <Button variant="primary" onClick={() => void store.copyLink(invoice)}>
                 <CopyIcon /> Скопировать
               </Button>
             </>
           }>
      <span className={styles.linkBox}>{store.linkFor(invoice)}</span>
    </Modal>
  )
})

const Row = observer(function Row({ item }: { item: InvoiceResponse }) {
  const { invoices: store } = useStores()
  const view = INVOICE_VIEW[item.status]
  const coin = item.crypto_currency ? coinOf(item.crypto_currency) : null

  return (
    <li className={styles.row}>
      <span className={styles.main}>
        <span className={styles.amount}>{fiat(item.amount_fiat, item.fiat_currency)}</span>

        {/* Криптосумма появляется только после выбора сети покупателем:
            до этого её нет и придумывать её нечем. */}
        {coin && item.crypto_amount ? (
          <span className={styles.meta}>
            {crypto(item.crypto_amount)} {coin.ticker} · {networkOf(item.crypto_currency!)}
          </span>
        ) : null}

        {/* Оплаченная часть показывается, только когда деньги пришли:
            «Оплачено 0» на неоплаченном счёте читается как ошибка. */}
        {item.paid_amount && Number(item.paid_amount) > 0 && coin ? (
          <span className={styles.meta}>
            Получено {crypto(item.paid_amount)} {coin.ticker}
          </span>
        ) : null}

        <span className={styles.meta}>
          Создан {when(item.created_at)} · действует до {when(item.expires_at)}
        </span>
        <span className={styles.mono}>{item.id}</span>
      </span>

      <span className={styles.side}>
        <span className={`${styles.status} ${styles[view.tone]}`}>
          <span className={styles.dot} aria-hidden="true" /> {view.text}
        </span>
        <Button variant="secondary" onClick={() => void store.copyLink(item)}>
          <CopyIcon /> Ссылка
        </Button>
      </span>
    </li>
  )
})

function when(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date).replace(', ', ' / ')
}
